import { getFormattedDateByTimezone, isFetchDatesParamsValid, mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Pane from 'components/Common/Pane'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useShareModalConfig from 'hooks/useShareModalConfig'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import ExperienceDetailsAboutLocationMap from '../ExperienceDetailsModal/About/ExperienceDetailsAboutLocationMap'
import ExperienceYAMLList from '../ExperienceLists/ExperienceYAMLList'
import ExperienceOfferPageTicketSelection from './ExperienceOfferPageTicketSelection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperienceOfferPageOverview from './ExperienceOfferPageOverview'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import TextButton from 'LuxKit/Button/TextButton'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import HeadData from 'components/Common/HeadData'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { experienceViewed } from 'actions/ExperienceActions'
import { useRemoveStateQueryParams } from './utils'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import { useSyncExperienceBookmarkWithPageState } from 'tripPlanner/hooks/bookmarks/useSyncExperienceBookmarkWithPageState'
import ExperienceOfferPageUnavailableTickets from './ExperienceOfferPageUnavailableTickets'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import ExperienceOfferPageUnavailableHeader from './ExperienceOfferPageUnavailableHeader'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import useExperiencePlace from 'hooks/Experiences/useExperiencePlace'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import Places from 'constants/places'
import { DMY_CASUAL_LONG_FORMAT } from 'constants/dateFormats'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ExperienceOfferPageBnblAvailable from './ExperienceOfferPageBnblAvailable'
import ExperienceBnblModal from '../ExperienceBnblModal/ExperienceBnblModal'
import ExperienceGiveAsGiftModal from '../ExperienceGiveAsGiftModal/ExperienceGiveAsGiftModal'
import ExperienceOfferPageTransferSelection from './ExperienceOfferPageTransferSelection'
import UserReviewList from 'components/OfferPage/NavigableOfferContent/UserReviewList'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import Accordion from 'LuxKit/Accordion'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { experienceOfferClicked } from 'analytics/eventDefinitions'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import type { ExperienceEventDefinitionKey } from 'analytics/snowplow/helpers'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import ModalContext from 'contexts/ModalContext'
import CurrencyContext from 'contexts/currencyContext'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import SavePromptExperience from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptExperience'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { FormattedMessage, useIntl } from 'react-intl'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import NumberRating from 'components/Common/NumberRating'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SummarySection = styled.div`
  display: grid;
  gap: ${rem(24)};
  grid-template:
    "action-group" auto
    "page-content" auto
    "page-overview" auto / 1fr;

  ${mediaQueryUp.tablet} {
    row-gap: ${rem(8)};
    column-gap: ${rem(32)};
    grid-template:
      "action-group action-group" auto
      "page-content page-overview" auto / 1fr ${rem(400)};
  }
`

const ExperienceOfferPageOverviewArea = styled.div`
  grid-area: page-overview;
`

const VerticalSpacerArea = styled(VerticalSpacer)`
  grid-area: page-content;
`

const ActionGroupArea = styled(Group)`
  grid-area: action-group;
`

const AboutItem = styled(VerticalSpacer)`
  padding: ${rem(56)} 0;
`

const IntroSection = styled(Pane)`
  padding-block-end: ${rem(96)};

  ${mediaQueryUp.tablet} {
    padding-block-start: ${rem(32)};
  }
`

const TicketSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const AboutSection = styled(LayoutContainer)``

const YAMLSection = styled(LayoutContainer)`
  padding-block: ${rem(40)};
  padding-block-end: ${rem(40)};

  > * + * {
    margin-block-start: ${rem(32)};
  }
`

const Breadcrumbs = styled(Pane)`
  ${mediaQueryUp.tablet}{
    padding: ${rem(24)} 0;
  }
`

const WrapperList = styled.div`
  > div ul {
    margin: 0;
  }
`

const MainContent = styled(Group)`
  flex-grow: 1;
`

export const MAP_TITLE_TO_EVENT: { [key: string]: { open: ExperienceEventDefinitionKey, close: ExperienceEventDefinitionKey } } = {
  'How to redeem': {
    open: 'INFO_HOW_TO_REDEEM_OPEN',
    close: 'INFO_HOW_TO_REDEEM_CLOSE',
  },
  'Cancellation policy': {
    open: 'INFO_CANCELLATION_POLICY_OPEN',
    close: 'INFO_CANCELLATION_POLICY_CLOSE',
  },
  Availability: {
    open: 'INFO_AVAILABILITY_OPEN',
    close: 'INFO_AVAILABILITY_CLOSE',
  },
  'Conditions of offer': {
    open: 'INFO_CONDITIONS_OF_OFFER_OPEN',
    close: 'INFO_CONDITIONS_OF_OFFER_CLOSE',
  },
  'Vendor information': {
    open: 'INFO_VENDOR_INFORMATION_OPEN',
    close: 'INFO_VENDOR_INFORMATION_CLOSE',
  },
  'The fine print': {
    open: 'INFO_FINE_PRINT_INFORMATION_OPEN',
    close: 'INFO_FINE_PRINT_INFORMATION_CLOSE',
  },
}

// Temporary solution for selling budled Derbysoft offers
const boxOfficeOfferIds = ['dby:176e6b74-1611-42a8-80f7-964d8f3ed857', 'dby:05e08d61-7750-47a7-958d-13d9a27f2f3b']

interface MappedStateToProps {
  isUserReviewsEnabled: boolean
  previousRoute: string
  locationHash: string
  search: string
  previewMode: boolean
  headlessMode: boolean
  isSpoofAccess: boolean
}

interface Props {
  experience: App.ExperienceOffer
}

function ExperienceOfferPageContent(props: Props & MappedStateToProps) {
  const {
    experience,
    isUserReviewsEnabled,
    previousRoute,
    locationHash,
    search,
    previewMode,
    headlessMode,
    isSpoofAccess,
  } = props

  const ticketOptionsRef = useRef<HTMLDivElement>(null)
  const ticketSelectionFormRef = useRef<HTMLFormElement>(null)
  const dispatch = useAppDispatch()
  const currentCurrency = useContext(CurrencyContext)
  const state = useContext(ExperienceOfferPageStateContext)
  const experienceStateRef = useRef(state)
  const trackEvent = useTrackExperienceOfferEvent()
  const intl = useIntl()

  useRemoveStateQueryParams(search)
  useSyncExperienceBookmarkWithPageState(experience, state)
  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  useEffect(() => {
    experienceStateRef.current = state
  }, [state])

  useEffect(() => {
    if (locationHash === '#ticket-options' || previousRoute?.includes('/booking/payment')) {
      // forcing anchor to ticket-options id
      requestAnimationFrame(() => window.scrollTo(0, ticketOptionsRef.current?.offsetTop || 0))
    }
  }, [locationHash, previousRoute])

  useEffect(() => {
    fireEcommerceGTMEvent(experienceOfferClicked(experience, currentCurrency))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentCurrency Currency is set in the SSR
  }, [experience.id])

  useEffect(() => {
    dispatch(experienceViewed(experience))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience.id])

  const view = useMemo(() => mapExperienceToBookingView(experience), [experience])

  const shareConfig = useShareModalConfig({
    image: experience.images[0],
    type: 'experience',
    offerId: props.experience.id,
  })

  const place = useExperiencePlace(experience)
  const categoryCodes = experience.primaryCategory?.parentId ?? experience.primaryCategory?.id
  const YAMLList = useExperienceSearchList({
    placeId: place?.id,
    categoryCodes: categoryCodes ? [categoryCodes] : undefined,
  })

  const [searchQuery] = useExperienceSearchUrl({ latLong: experience.location })
  const experiencesBreadcrumbLabel = intl.formatMessage({
    id: 'kDyMoh3MS2',
    defaultMessage: 'Experiences',
    description: 'Breadcrumb label for experiences section on experience PDP',
  })

  const multipleLocationsLabel = intl.formatMessage({
    id: 'zy7QveRuS6',
    defaultMessage: 'Multiple Locations',
    description: 'Label shown when experience has multiple redemption locations',
  })

  const inclusionsLabel = intl.formatMessage({
    id: 'OLAtETZk7G',
    defaultMessage: 'Inclusions',
    description: 'Section heading for experience inclusions on PDP on the offer detail page',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'r4wAKOR2G0',
    defaultMessage: 'Show more',
    description: 'Button text to expand inclusions content on experience PDP',
  })

  const showLessLabel = intl.formatMessage({
    id: 'guwERRs21s',
    defaultMessage: 'Show less',
    description: 'Button text to collapse inclusions content on experience PDP',
  })

  const locationsLabel = intl.formatMessage({
    id: 'vpwstRgNbf',
    defaultMessage: 'Locations',
    description: 'Section heading when experience has multiple locations on PDP',
  })

  const locationLabel = intl.formatMessage({
    id: '1+etUkf9wH',
    defaultMessage: 'Location',
    description: 'Section heading for single experience location on PDP',
  })

  const breadcrumbs = useMemo((): Array<App.BreadcrumbItem> => {
    return [
      { text: experiencesBreadcrumbLabel, url: '/experiences' },
      // search query is applicable only on single location experiences
      ...(experience.redemptionLocations?.length > 1 ? [] : [
        { text: experience.location.name, url: searchQuery },
      ]),
      { text: experience.name },
    ]
  }, [experience, searchQuery, experiencesBreadcrumbLabel])

  const canPurchaseDisneyBoxOfficeOffer = () => {
    // If this offer is being accessed by a spoofed user, we want to allow them to purchase it
    if (isSpoofAccess) return true

    // if this offer is not being accessed by a spoofed user, we want to check if it's a box office offer
    // and if it's not, we want to allow the user to purchase it. If it is, we want the user to see a sold out offer
    return !boxOfficeOfferIds.includes(experience.id)
  }

  const isAvailable = (experience.status === 'ONLINE' || previewMode) && canPurchaseDisneyBoxOfficeOffer()

  // complimentary offers have zero price, we should block the booking through the standalone flow without showing the sold out messages
  const hasPrice = experience.price > 0

  const lastAvailableBookingDate = experience.expirationDate && experience.id.startsWith('led') ?
      getFormattedDateByTimezone(experience.expirationDate, experience.location.timezone, DMY_CASUAL_LONG_FORMAT) :
    undefined

  const [categorySearchLink] = useExperienceSearchUrl({
    categoryIds: experience.primaryCategory?.id ? [experience.primaryCategory?.id] : undefined,
    place: Places.Anywhere,
  })

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isAgentHubEnabled = config.agentHub.isEnabled
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(experience.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia

  const handleOnShare = useCallback(() => {
    trackEvent('SHARE_OPTION_SELECTED')
  }, [trackEvent])

  const handleTitleCopy = useCallback(() => {
    trackEvent('TITLE_COPIED')
  }, [trackEvent])

  const handleThingsToKnowOpen = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].open) {
      trackEvent(MAP_TITLE_TO_EVENT[title].open)
    }
  }, [trackEvent])

  const handleThingsToKnowClose = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].close) {
      trackEvent(MAP_TITLE_TO_EVENT[title].close)
    }
  }, [trackEvent])

  const showModal = useContext(ModalContext)

  const isTicketSelectionValid = useCallback(({ isGift, isBuyNowBookLater }: { isGift?: boolean, isBuyNowBookLater?: boolean }) => {
    if (!ticketSelectionFormRef.current) return true

    return isFetchDatesParamsValid(experience, {
      pickupPointId: experienceStateRef.current.pickupPoint?.id,
      redemptionLocationId: experienceStateRef.current.redemptionLocation?.id,
      ticketModesMap: experienceStateRef.current.ticketModesMap,
      isGift,
      isBuyNowBookLater,
    })
  }, [experience])

  const showBNBLModal = useCallback(async() => {
    if (!isTicketSelectionValid({ isBuyNowBookLater: true })) {
      ticketSelectionFormRef.current?.reportValidity()
      return
    }

    await showModal(<ExperienceBnblModal experienceView={view} />)
    trackEvent('BNBL_COMPONENT_CLOSED')
  }, [showModal, trackEvent, view, isTicketSelectionValid])

  const showGiftModal = useCallback(async() => {
    if (!isTicketSelectionValid({ isGift: true })) {
      ticketSelectionFormRef.current?.reportValidity()
      return
    }

    await showModal(<ExperienceGiveAsGiftModal experienceView={view} />)
    trackEvent('GIFT_COMPONENT_CLOSED')
  }, [showModal, trackEvent, view, isTicketSelectionValid])

  useEffect(() => {
    const { modal } = parseSearchString(search)
    if (modal === 'gifting') {
      showGiftModal()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const shouldShowToolTip = useScreenSizeUp('tablet')

  // TMP to hide inclusions label for klook single package offers
  const showInclusionsLabel = experience.provider !== 'klo' || experience.copy.included?.includes('Package:')

  const reviewsEnabled = isUserReviewsEnabled && isExperienceOfferRatingDisplayable(experience)

  return <>
    <TripGuard>
      <SavePromptExperience offer={experience} />
    </TripGuard>
    <HeadData
      title={experience.name}
      description={experience.copy.description}
      imageUrl={experience.images[0]?.url}
      imageId={experience.images[0]?.id}
      imageAlt={experience.images[0]?.title}
      noIndex={!isAvailable || experience.noIndex}
    />
    <ImmersiveSaveButtonPortal>
      <ExperienceBookmarkButton
        offer={experience}
      />
    </ImmersiveSaveButtonPortal>
    <MainContent direction="vertical" verticalAlign="space-between">
      <div>
        {!headlessMode && <Breadcrumbs>
          <LayoutContainer variant="large">
            <DesktopRelatedTravelItems relatedTravelItems={breadcrumbs} />
          </LayoutContainer>
        </Breadcrumbs>}
        {!isAvailable && <LayoutContainer>
          <ExperienceOfferPageUnavailableHeader experience={experience} />
        </LayoutContainer>}
        <ScrollAnchor id="offer-details" />
        <OfferPageGallery experience={experience} shareModalConfig={shareConfig} />
        <LayoutContainer variant="large">
          <IntroSection>
            <SummarySection>
              <ActionGroupArea direction="horizontal" horizontalAlign="end">
                <LabelGroup>
                  {!isAvailable && <StatusLabel variant="cancelled">{intl.formatMessage({
                    id: '52j+nvFn+h',
                    defaultMessage: 'Sold out',
                    description: 'Status label when experience offer is sold out on PDP',
                  })}
                  </StatusLabel>}
                </LabelGroup>
                <CSSBreakpoint min="tablet">
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={2}>
                    <TripGuard>
                      <ExperienceBookmarkButton offer={experience} />
                    </TripGuard>
                    <ShareModal shareConfig={shareConfig} onShare={handleOnShare} openOnHash="#share">
                      <ShareWithFriendsButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} onClick={() => trackEvent('SHARE_BUTTON_CLICKED')} showToolTip={shouldShowToolTip} />
                    </ShareModal>
                    {isExportMediaEnabled && canAgentExportMedia && <TripGuard inverse>
                      <ExportMediaContentButton href={mediaExportURL} target="_blank" />
                    </TripGuard>}
                    {isExportMediaEnabled && canAgentExportMedia && <TripGuard>
                      <ExportMediaContentButton href={mediaExportURL} target="_blank" />
                    </TripGuard>}
                  </Group>
                </CSSBreakpoint>
              </ActionGroupArea>
              <VerticalSpacerArea gap={24}>
                <ExperienceAppDiscountTag experienceOffer={experience} />
                <VerticalSpacerArea gap={16}>
                  <VerticalSpacer gap={8}>
                    <BodyTextBlock variant="medium" weight="bold" startIcon={<LineMapMarkerIcon aria-hidden="true" />}>
                      {experience.redemptionLocations?.length > 1 ? multipleLocationsLabel : experience.location.name}
                      {experience.primaryCategory && <>
                        <BodyText variant="medium" as="span">{formatMiddleDotSeparator(intl)}</BodyText>
                        <TextLink size="medium" to={categorySearchLink}>
                          {experience.primaryCategory.name}
                        </TextLink>
                      </>}
                    </BodyTextBlock>
                    <Heading as="h3" variant="heading2" onCopy={handleTitleCopy}>{experience.name}</Heading>
                    {lastAvailableBookingDate && <BodyTextBlock variant="large" startIcon={<LineCalendarIcon aria-hidden="true" />}>
                      <FormattedMessage
                        id="rAVsvZQ9ef"
                        defaultMessage="Book up until {date}"
                        description="Last available booking date information displayed on the experience product detail page"
                        values={{
                          date: <b><u>{lastAvailableBookingDate}</u></b>,
                        }}
                      />
                    </BodyTextBlock>}
                    {reviewsEnabled && (
                      <NumberRating
                        rating={experience.rating?.score}
                        total={experience.rating?.reviewsTotal}
                        ratingSubtitle={experience.rating?.reviewsSubtitle}
                        variant="small"
                        numberPosition="start"
                      />
                    )}
                  </VerticalSpacer>
                  <MarkdownRender content={experience.copy.dealDescription ?? ''} fontSize="medium" />
                  {!experience.copy.dealDescription && !!experience.copy.highlights.length && <WrapperList>
                    {experience.copy.highlights.map((highlight, index) => <MarkdownRender key={index} content={highlight} fontSize="medium" type="compact" />)}
                  </WrapperList>}
                  {experience.copy.included && (
                    <WrapperList>
                      {showInclusionsLabel && <BodyText as="h6" variant="large" weight="bold">{inclusionsLabel}</BodyText>}
                      <LargeContentToggle
                        maxHeight={200}
                        showMoreText={showMoreLabel}
                        showLessText={showLessLabel}
                      >
                        <MarkdownRender type="compact" content={experience.copy.included} fontSize="medium" />
                      </LargeContentToggle>
                    </WrapperList>
                  )}
                </VerticalSpacerArea>
              </VerticalSpacerArea>
              <ExperienceOfferPageOverviewArea>
                <ExperienceOfferPageOverview
                  experience={experience}
                  onGiveAsGift={showGiftModal}
                />
              </ExperienceOfferPageOverviewArea>
            </SummarySection>
          </IntroSection>
        </LayoutContainer>
      </div>
      <div>
        <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="experience" saveUpToPercent={experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experience.discount : undefined} />}>
          {(hasPrice || !isAvailable) && <TicketSection background="neutral-six" id="ticket-options" ref={ticketOptionsRef}>
            <LayoutContainer variant="medium">
              <VerticalSpacer gap={40}>
                <Heading as="h3" variant="heading2" align="center">{intl.formatMessage({
                  id: 'op+0Atpbu3',
                  defaultMessage: 'Pricing & availability',
                  description: 'Section heading for pricing and availability on experience PDP',
                })}
                </Heading>
                {!isAvailable && <ExperienceOfferPageUnavailableTickets experience={experience} />}
                {isAvailable && experience.productType !== 'transfer' &&
                  <ExperienceOfferPageTicketSelection
                    ref={ticketSelectionFormRef}
                    experienceView={view}
                  />}
                {isAvailable && experience.productType === 'transfer' &&
                  <ExperienceOfferPageTransferSelection
                    experienceView={view}
                  />}
                {/* we don't support bnbl and gift for transfer offers yet */}
                {isAvailable && (experience.allowBuyNowBookLater || experience.giftType) && experience.productType !== 'transfer' &&
                  <ExperienceOfferPageBnblAvailable
                    experience={experience}
                    onBuyNowBnbl={showBNBLModal}
                    onGiveAsGift={showGiftModal}
                  />}
              </VerticalSpacer>
            </LayoutContainer>
          </TicketSection>}
        </SoftOfferPageWalledGarden>
        <SoftOfferPageWalledGarden>
          <AboutSection variant="medium">
            <AboutItem gap={16}>
              <Heading variant="heading2" align="center">{experience.redemptionLocations?.length > 1 ? locationsLabel : locationLabel}</Heading>
              <ExperienceDetailsAboutLocationMap
                experienceView={view}
                selectedLocation={state.redemptionLocation}
              />
            </AboutItem>

            {experience.copy.thingsToKnow.length > 0 && <AboutItem gap={8}>
              <Heading as="h3" variant="heading3" align="center">{intl.formatMessage({
                id: 'dbEvRNbGSZ',
                defaultMessage: 'Things to know',
                description: 'Section heading for important information about the experience on PDP',
              })}
              </Heading>
              <div>
                {experience.copy.thingsToKnow.map(item => (
                  <Accordion
                    key={item.title}
                    onOpen={() => handleThingsToKnowOpen(item.title)}
                    onClose={() => handleThingsToKnowClose(item.title)}
                    heading={item.title}
                    size="large"
                  >
                    <LayoutContainer>
                      <MarkdownRender content={item.content} type="compact" fontSize="medium" />
                    </LayoutContainer>
                  </Accordion>
                ))}
              </div>
            </AboutItem>}
            {reviewsEnabled && (
              <AboutItem gap={24}>
                <Heading as="h3" variant="heading3" align="center">{intl.formatMessage({
                  id: 'qK7r5+Ibdw',
                  defaultMessage: 'Reviews',
                  description: 'Section heading for user reviews on experience PDP',
                })}
                </Heading>
                <UserReviewList experience={experience} reviewsThreshold={0} />
              </AboutItem>
            )}
          </AboutSection>
        </SoftOfferPageWalledGarden>
        <SoftOfferPageWalledGarden>
          {isAvailable && YAMLList?.ids.length >= 5 && <YAMLSection>
            <Group horizontalAlign="space-between" verticalAlign="center" direction="horizontal">
              <Heading as="h4" variant="heading3">{intl.formatMessage({
                id: '6LtmjrSHCk',
                defaultMessage: 'You may also like',
                description: 'Section heading for recommended similar experiences on PDP',
              })}
              </Heading>
              <TextButton
                kind="tertiary"
                horizontalOutdent="end"
                to={searchQuery}
              >
                {intl.formatMessage({
                  id: '5Eysvi8V+d',
                  defaultMessage: 'View all',
                  description: 'Link to view all similar experience offers on PDP on the offer detail page',
                })}
              </TextButton>
            </Group>
            <ExperienceYAMLList experience={experience} />
          </YAMLSection>}
        </SoftOfferPageWalledGarden>
      </div>
    </MainContent>
  </>
}

const mapStateToProps = (state: App.State): MappedStateToProps => ({
  isUserReviewsEnabled: config.EXPERIENCES_REVIEWS_ENABLED,
  previousRoute: state.routeHistory.prevPath,
  locationHash: state.router.location.hash,
  search: state.router.location.search,
  previewMode: state.system.previewMode,
  headlessMode: state.system.headlessMode,
  isSpoofAccess: state.auth.account.isSpoofed,
})

export default connect(mapStateToProps)(ExperienceOfferPageContent)
